'use client';

import { useMemo, useState } from 'react';
import { useTranslations } from 'next-intl';

import { HelpCategoryCard } from './HelpCategoryCard';

export interface HelpCenterCategory {
	slug: string;
	code: string;
	title: string;
	articles: { slug: string; title: string }[];
}

interface HelpCenterProps {
	categories: HelpCenterCategory[];
	/** Localized "Help Center" label shown above the heading. */
	rootLabel: string;
}

export function HelpCenter({ categories, rootLabel }: HelpCenterProps) {
	const t = useTranslations('Help');
	const [q, setQ] = useState('');
	const query = q.trim().toLowerCase();

	const filtered = useMemo(() => {
		if (!query) return categories.map((c) => ({ ...c, shown: c.articles }));
		return categories
			.map((c) => {
				const catMatch = c.title.toLowerCase().includes(query);
				const shown = catMatch
					? c.articles
					: c.articles.filter((a) => a.title.toLowerCase().includes(query));
				return { ...c, shown };
			})
			.filter((c) => c.shown.length > 0);
	}, [categories, query]);

	const resultCount = query
		? t('center.foundCount', {
				count: filtered.reduce((s, c) => s + c.shown.length, 0),
			})
		: t('center.catCount', { count: categories.length });

	return (
		<div className="mt-8 sm:mt-10">
			<div className="label-mono">{rootLabel}</div>
			<h1 className="3xl:text-5xl mt-2 text-3xl font-light tracking-tight sm:text-4xl">
				{t('center.categoriesHeading')}
			</h1>

			{/* Search */}
			<div className="border-fg bg-surface mt-6 flex max-w-xl items-center gap-3 border px-4">
				<span className="text-muted font-mono text-sm" aria-hidden="true">
					⌕
				</span>
				<input
					type="search"
					value={q}
					onChange={(e) => setQ(e.target.value)}
					placeholder={t('center.searchPlaceholder')}
					aria-label={t('center.searchPlaceholder')}
					className="text-fg min-w-0 flex-1 border-0 bg-transparent py-3.5 text-[15px] outline-none"
				/>
				<span className="label-mono shrink-0">{resultCount}</span>
			</div>
			<div className="border-border mt-6 border-t" />

			{/* Category grid */}
			{filtered.length > 0 ? (
				<div className="mt-6 grid grid-cols-1 gap-3 sm:gap-4 md:grid-cols-2 xl:grid-cols-3">
					{filtered.map((c) => (
						<HelpCategoryCard
							key={c.slug}
							code={c.code}
							title={c.title}
							countLabel={t('center.artShort', { count: c.articles.length })}
							articles={c.shown}
						/>
					))}
				</div>
			) : (
				<div className="border-border text-muted mt-6 border border-dashed p-8 text-center font-mono text-[13px] leading-relaxed tracking-wide">
					{t('center.noResults', { q: q.trim() })}
				</div>
			)}
		</div>
	);
}
